import React from 'react';
import { useApp } from '../context/AppContext';
import { getColors } from '../theme';

// Shared notebook primitives: ruled rows with a margin column, ink-rule section heads,
// square ink checkboxes and thin progress bars. See CoreDesignPrinciples.md.

export const useNotebookColors = () => getColors(useApp().isDark);

// In-app pages use the compact head so content starts high; `size="lg"` is for Today and public pages.
// `actions` sit on the title's baseline and wrap beneath it on narrow screens.
export const PageHead = ({ meta, title, actions, size = 'md', children }) => {
  const colors = useNotebookColors();
  const large = size === 'lg';
  return (
    <div className={large ? 'pt-6 pb-6 sm:pt-8' : 'pt-5 pb-5 sm:pt-6'}>
      {meta && (
        <p className="font-mono text-xs mb-2" style={{ color: colors.textSecondary }}>{meta}</p>
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <h1
          className={`${large ? 'text-[44px] sm:text-[64px]' : 'text-[32px] sm:text-[36px]'} font-semibold leading-[.95]`}
          style={{ color: colors.textPrimary, letterSpacing: large ? '-0.045em' : '-0.035em' }}
        >
          {title}
        </h1>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
};

export const Section = ({ title, aside, children, className = '', spacing = 'mt-8' }) => {
  const colors = useNotebookColors();
  return (
    <section className={`${spacing} ${className}`}>
      <div
        className="flex justify-between items-baseline gap-4 pb-2"
        style={{ borderBottom: `1px solid ${colors.textPrimary}` }}
      >
        <h2 className="text-[15px] font-semibold tracking-tight" style={{ color: colors.textPrimary }}>{title}</h2>
        {aside && <span className="font-mono text-xs text-right" style={{ color: colors.textSecondary }}>{aside}</span>}
      </div>
      {children}
    </section>
  );
};

const ROW_GRID = 'nb-row grid grid-cols-[50px_24px_minmax(0,1fr)_auto] sm:grid-cols-[64px_28px_minmax(0,1fr)_auto] gap-x-2.5 sm:gap-x-3.5 items-center min-h-[48px]';

export const Row = ({ margin, marginStrong, mark, meta, children, onClick, className = '', style, title }) => {
  const colors = useNotebookColors();
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      className={`${ROW_GRID} w-full text-left transition-colors duration-150 ${onClick ? 'cursor-pointer notebook-row-hover' : ''} ${className}`}
      style={{ borderBottom: `1px solid ${colors.border}`, color: colors.textPrimary, ...style }}
    >
      <span
        className="self-stretch flex items-center font-mono text-xs tabular-nums"
        style={{ borderRight: `1px solid ${colors.border}`, color: marginStrong ? colors.textPrimary : colors.textSecondary }}
      >
        {margin}
      </span>
      <span className="flex items-center justify-center">{mark}</span>
      <span className="nb-cell min-w-0 py-3" style={{ color: colors.textPrimary }}>{children}</span>
      <span className="flex items-center gap-3.5 justify-self-end">{meta}</span>
    </Tag>
  );
};

// The next line of a list: type and press Enter to add, like writing the next line in a notebook.
// With `autoFocus`, the row takes focus on load where there's a mouse and keyboard, so the caret is
// already blinking there; clicking elsewhere releases it. Once there's text, an ink Add button
// appears. `onDetails(text)` ("More options") opens the full dialog with what's typed so far.
// When `blocked` (plan limit), focusing the row calls `onBlocked` instead.
// Layouts: with `margin`, a ruled row with the margin column; without, a ruled row with the
// mark first; `bare` returns just [mark, input, actions] for a caller that aligns them itself.
// With `interpret` (see agent/quickAdd.js), text that looks like it has a date or detail is
// read by the assistant first: the row shows what it understood, and Enter again adds that.
export const QuickAddRow = ({ placeholder, mark, margin, onAdd, onDetails, detailsWhenEmpty = true, hint, blocked, onBlocked, bare = false, autoFocus = false, interpret }) => {
  const colors = useNotebookColors();
  const [text, setText] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const [reading, setReading] = React.useState(false);
  const [preview, setPreview] = React.useState(null); // { text, summary, fields }
  const inputRef = React.useRef(null);
  const textRef = React.useRef('');
  textRef.current = text;

  // Only the visible copy (pages render phone and desktop layouts), only with a fine pointer so
  // phones don't pop the keyboard, and without scrolling the page to the row.
  React.useEffect(() => {
    const input = inputRef.current;
    if (!autoFocus || blocked || !input || input.offsetParent === null) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    input.focus({ preventScroll: true });
  }, [autoFocus, blocked]);

  const clear = () => {
    setText('');
    setPreview(null);
    inputRef.current?.focus();
  };

  const addAsTyped = () => {
    const value = text.trim();
    if (!value) return;
    onAdd(value);
    clear();
  };

  const submit = async () => {
    const value = text.trim();
    if (!value || reading) return;
    if (preview && preview.text === value) {
      interpret.apply(preview.fields);
      clear();
      return;
    }
    if (!interpret || !interpret.shouldTry(value)) {
      addAsTyped();
      return;
    }
    setReading(true);
    try {
      const result = await interpret.run(value);
      // Only show it if the text is still what was read
      if (textRef.current.trim() === value) setPreview({ text: value, ...result });
    } catch {
      // The assistant couldn't help: add it as typed, as the row always did
      if (textRef.current.trim() === value) addAsTyped();
    } finally {
      setReading(false);
    }
  };

  const hasText = text.trim().length > 0;
  const keepFocus = (e) => e.preventDefault(); // buttons act without blurring the input

  const showingPreview = preview && preview.text === text.trim();

  const meta = (
    <>
      {showingPreview && (
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={addAsTyped}
          className="font-mono text-xs whitespace-nowrap underline underline-offset-4"
          style={{ color: colors.textSecondary, textDecorationColor: colors.borderStrong }}
        >
          As typed
        </button>
      )}
      {hint && focused && hasText && !showingPreview && !reading && (
        <span className="hidden sm:inline font-mono text-[11px]" style={{ color: colors.textMuted }}>{hint}</span>
      )}
      {onDetails && (hasText || detailsWhenEmpty) && !showingPreview && (
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={() => (blocked ? onBlocked?.() : onDetails(text.trim()))}
          className="font-mono text-xs whitespace-nowrap underline underline-offset-4"
          style={{ color: colors.textSecondary, textDecorationColor: colors.borderStrong }}
        >
          More options
        </button>
      )}
      {hasText && (
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={submit}
          className="font-mono text-xs px-2.5 py-1"
          style={{ backgroundColor: colors.accent, color: colors.onAccent }}
        >
          Add
        </button>
      )}
    </>
  );

  const input = (
    <span className="flex-1 min-w-0 flex flex-col justify-center">
      <input
        ref={inputRef}
        type="text"
        value={text}
        placeholder={blocked ? 'Limit reached · upgrade to add more' : placeholder}
        aria-label={placeholder}
        enterKeyHint="enter"
        onChange={(e) => { setText(e.target.value); setPreview(null); }}
        onFocus={(e) => {
          if (blocked) {
            e.target.blur();
            onBlocked?.();
            return;
          }
          setFocused(true);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            setText('');
            setPreview(null);
            e.target.blur();
          }
        }}
        className="quick-add-input nb-cell w-full min-w-0 bg-transparent outline-none text-base py-3"
        style={{ color: colors.textPrimary, '--placeholder': colors.textMuted }}
      />
      {(reading || showingPreview) && (
        <span className="font-mono text-[11px] -mt-2 pb-2 truncate agent-fade" style={{ color: reading ? colors.textMuted : colors.textPrimary }} aria-live="polite">
          {reading ? 'reading…' : `→ ${preview.summary} · ↵ to add`}
        </span>
      )}
    </span>
  );

  const markSlot = <span className="flex items-center justify-center flex-shrink-0" style={{ color: colors.textMuted }}>{mark}</span>;
  const metaSlot = <span className="flex items-center gap-3.5 justify-self-end flex-shrink-0">{meta}</span>;

  if (bare) {
    return <>{markSlot}{input}{metaSlot}</>;
  }

  const rule = { borderBottom: `1px solid ${focused ? colors.textPrimary : colors.border}` };

  if (margin === undefined) {
    return (
      <div className="nb-row grid grid-cols-[24px_minmax(0,1fr)_auto] gap-x-3 items-center min-h-[48px] transition-colors" style={rule}>
        {markSlot}
        {input}
        {metaSlot}
      </div>
    );
  }

  return (
    <div className={`${ROW_GRID} transition-colors`} style={rule}>
      <span
        className="self-stretch flex items-center font-mono text-xs tabular-nums"
        style={{ borderRight: `1px solid ${colors.border}`, color: colors.textSecondary }}
      >
        {margin}
      </span>
      {markSlot}
      {input}
      {metaSlot}
    </div>
  );
};

export const InkBox = ({ checked, size = 18 }) => {
  const colors = useNotebookColors();
  return (
    <span
      aria-hidden="true"
      className="grid place-items-center flex-shrink-0 transition-colors duration-200"
      style={{ width: size, height: size, border: `1.5px solid ${colors.textPrimary}`, backgroundColor: checked ? colors.textPrimary : 'transparent' }}
    >
      <svg viewBox="0 0 16 16" width={size - 4} height={size - 4}>
        <path
          d="M3.5 8.4 6.6 11.4 12.5 4.8"
          pathLength="1"
          fill="none"
          stroke={colors.onAccent}
          strokeWidth="2"
          strokeLinecap="square"
          className={`ink-check ${checked ? 'on' : ''}`}
        />
      </svg>
    </span>
  );
};

export const Bar = ({ value, max = 260, color }) => {
  const colors = useNotebookColors();
  return (
    <span className="block relative" style={{ height: 2, backgroundColor: colors.border, maxWidth: max }}>
      <span
        className="absolute inset-y-0 left-0 transition-[width] duration-500"
        style={{ width: `${Math.max(0, Math.min(100, value))}%`, backgroundColor: color || colors.textPrimary }}
      />
    </span>
  );
};

export const InkNote = ({ children }) => {
  const colors = useNotebookColors();
  return (
    <div className="pt-4">
      <p className="text-[19px] italic mb-1 ink-note-text" style={{ fontFamily: "'Noto Serif', Georgia, serif", color: colors.textPrimary }}>
        {children}
      </p>
      <svg viewBox="0 0 240 10" preserveAspectRatio="none" aria-hidden="true" className="block w-60 max-w-full h-2.5 overflow-visible">
        <path className="ink-underline" pathLength="1" d="M2 7 C 48 2, 94 9, 138 5 S 208 3, 238 6" fill="none" stroke={colors.textPrimary} strokeWidth="1.5" />
      </svg>
    </div>
  );
};
